import { ArrowLeft, ArrowUpRight, BadgeCheck, LoaderCircle, LockKeyhole, MailCheck } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { FlowSphereLogo } from "../components/FlowSphereLogo";

const formatError = (detail) => Array.isArray(detail) ? detail.map((item) => item.msg).join(" ") : typeof detail === "string" ? detail : "Please check your details";

export default function Login({ onLogin, navigate }) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("login"); // login | forgot | sent
  const [resetEmail, setResetEmail] = useState("");

  const submit = async (event) => {
    event.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/auth/login", form); onLogin(data); toast.success("Welcome back to FlowSphere"); }
    catch (error) { toast.error(formatError(error.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const sendReset = async (event) => {
    event.preventDefault(); setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: resetEmail });
      setMode("sent");
    } catch (error) { toast.error(formatError(error.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return <main className="login-page">
    <section className="login-visual"><button className="login-brand" data-testid="login-home-button" onClick={() => navigate("/")}><FlowSphereLogo variant="login"/></button><div><span className="section-kicker">YOUR CONFIGURED WORKSPACE</span><h1>Everything in place.<br /><em>Ready for business.</em></h1><p>Return to invoices, inventory, payments and finances — already organized around your operation.</p><div className="login-proof"><BadgeCheck size={18}/><span><strong>Personalized setup</strong><small>Your workspace keeps the preferences prepared for your business.</small></span></div></div></section>
    <section className="login-form-panel">
      <button className="back-link" data-testid="login-back-button" onClick={() => navigate("/")}><ArrowLeft size={16}/> Back to FlowSphere</button>
      <div className="login-form-wrap">
        {mode === "login" && <>
          <LockKeyhole size={28}/><span className="section-kicker">CUSTOMER LOGIN</span>
          <h2 data-testid="login-heading">Welcome back.</h2>
          <p>Use the login details provided after your business setup.</p>
          <form data-testid="login-form" onSubmit={submit}>
            <label>Email<input data-testid="login-email-input" type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} required/></label>
            <label>Password<input data-testid="login-password-input" type="password" value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} required/></label>
            <button className="white-button" data-testid="login-submit-button" disabled={busy}>{busy ? <LoaderCircle className="spin"/> : <>Login to FlowSphere <ArrowUpRight size={17}/></>}</button>
          </form>
          <button className="demo-access" data-testid="login-forgot-password-button" onClick={() => { setResetEmail(form.email); setMode("forgot"); }}>Forgot your password?</button>
          <div className="new-business-prompt"><span>New to FlowSphere?</span><button data-testid="login-setup-business-button" onClick={() => navigate("/setup")}>Set Up My Business <ArrowUpRight size={15}/></button></div>
        </>}

        {mode === "forgot" && <>
          <LockKeyhole size={28}/><span className="section-kicker">PASSWORD RESET</span>
          <h2 data-testid="forgot-heading">Reset your password.</h2>
          <p>Enter your account email and we'll send you a secure link to set a new password.</p>
          <form data-testid="forgot-form" onSubmit={sendReset}>
            <label>Email<input data-testid="forgot-email-input" type="email" value={resetEmail} onChange={(event) => setResetEmail(event.target.value)} required/></label>
            <button className="white-button" data-testid="forgot-submit-button" disabled={busy}>{busy ? <LoaderCircle className="spin"/> : <>Send reset link <ArrowUpRight size={17}/></>}</button>
          </form>
          <button className="demo-access" data-testid="forgot-back-to-login-button" onClick={() => setMode("login")}>Back to login</button>
        </>}

        {mode === "sent" && <>
          <MailCheck size={28}/><span className="section-kicker">CHECK YOUR EMAIL</span>
          <h2 data-testid="forgot-sent-heading">Reset link sent.</h2>
          <p data-testid="forgot-sent-message">If an account exists for <strong>{resetEmail}</strong>, we've sent a secure link to reset your password. It expires in 1 hour. Please check your inbox (and spam folder).</p>
          <button className="white-button" data-testid="forgot-return-login-button" style={{ marginTop: 28 }} onClick={() => setMode("login")}>Return to login <ArrowUpRight size={17}/></button>
        </>}
      </div>
    </section>
  </main>;
}
